import React from 'react';
import styled, { css } from 'styled-components';

export type CsvOperationProgress = {
  percent: number;
  label: string;
};

type Props = {
  progress: CsvOperationProgress;
  variant?: 'inline' | 'default';
  'data-testid'?: string;
};

const CsvOperationProgressBar = ({
  progress,
  variant = 'default',
  'data-testid': dataTestId = 'csv-operation-progress',
}: Props) => {
  const percent = Math.min(100, Math.max(0, progress.percent));

  return (
    <ProgressCard $variant={variant} data-testid={dataTestId}>
      <ProgressHeader>
        <ProgressLabel $variant={variant}>{progress.label}</ProgressLabel>
        <ProgressPercent
          data-testid={`${dataTestId}-percent`}
          $variant={variant}
        >
          {Math.round(percent)}%
        </ProgressPercent>
      </ProgressHeader>
      <ProgressTrack $variant={variant}>
        <ProgressFill
          data-testid={`${dataTestId}-fill`}
          style={{ width: `${percent}%` }}
        />
      </ProgressTrack>
    </ProgressCard>
  );
};

export default CsvOperationProgressBar;

const ProgressCard = styled.div<{ $variant: 'inline' | 'default' }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $variant }) => ($variant === 'inline' ? '6px' : '10px')};
  width: 100%;

  ${({ $variant, theme }) =>
    $variant === 'default' &&
    css`
      padding: 12px 16px;
      border-radius: 8px;
      background-color: ${theme.colors.primary[10]};
      border: 1px solid ${theme.colors.primary[20]};
    `}
`;

const ProgressHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  width: 100%;
`;

const ProgressLabel = styled.span<{ $variant: 'inline' | 'default' }>`
  color: ${({ theme }: any) => theme.colors.primary[80]};
  font-size: ${({ $variant }) => ($variant === 'inline' ? '13px' : '14px')};
  font-weight: 400;
  line-height: 1.4;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ProgressPercent = styled.span<{ $variant: 'inline' | 'default' }>`
  color: ${({ theme }: any) => theme.colors.interactive[100]};
  font-size: ${({ $variant }) => ($variant === 'inline' ? '13px' : '14px')};
  font-weight: 600;
  line-height: 1.4;
  flex-shrink: 0;
`;

const ProgressTrack = styled.div<{ $variant: 'inline' | 'default' }>`
  width: 100%;
  height: ${({ $variant }) => ($variant === 'inline' ? '6px' : '8px')};
  border-radius: 999px;
  background-color: ${({ theme }: any) => theme.colors.primary[20]};
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: 999px;
  background-color: ${({ theme }: any) => theme.colors.interactive[100]};
  transition: width 0.2s linear;
`;
